import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  Pencil,
  Mail,
  Users as UsersIcon,
} from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import type { CustomerRow } from '@runik/shared';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { DataTable } from '../../../../components/data/DataTable';
import { EmptyState } from '../../../../components/data/EmptyState';
import { CustomerFormDialog } from '../../../../components/customers/CustomerFormDialog';
import { InvitationLinkDialog } from '../../../../components/customers/InvitationLinkDialog';
import { toast } from '../../../../components/ui/sonner';
import { ApiError } from '../../../../lib/auth/client';
import {
  customersKey,
  deleteCustomer,
  listCustomers,
  reissueInvitation,
} from '../../../../lib/queries/customers';

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function CustomersPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput, 300);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerRow | undefined>();
  const [invitation, setInvitation] = useState<{ activationUrl: string; expiresAt: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CustomerRow | null>(null);

  const query = { page, limit: 25, search: search || undefined };
  const customers = useQuery({
    queryKey: customersKey(query),
    queryFn: () => listCustomers(query),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => deleteCustomer(id),
    onSuccess: () => {
      toast.success('Müşteri silindi');
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Silinemedi');
    },
  });

  const reissueMutation = useMutation({
    mutationFn: (id: string) => reissueInvitation(id),
    onSuccess: (resp) => {
      setInvitation({ activationUrl: resp.activationUrl, expiresAt: resp.expiresAt });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Davet üretilemedi');
    },
  });

  const columns = useMemo<ColumnDef<CustomerRow, unknown>[]>(
    () => [
      { accessorKey: 'companyName', header: 'Şirket' },
      {
        accessorKey: 'billingEmail',
        header: 'Fatura E-postası',
        cell: ({ row }) => row.original.billingEmail ?? '—',
      },
      {
        id: 'authable',
        header: 'Durum',
        cell: ({ row }) =>
          row.original.authable ? (
            <Badge variant="success">Aktif</Badge>
          ) : (
            <Badge variant="warning">Beklemede</Badge>
          ),
      },
      {
        accessorKey: 'credentialsCount',
        header: 'Kimlik Bilgisi',
        cell: ({ row }) => row.original.credentialsCount,
      },
      {
        accessorKey: 'totalDue',
        header: 'Bekleyen Borç',
        cell: ({ row }) => (Number(row.original.totalDue) > 0 ? row.original.totalDue : '—'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const c = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => {
                    setEditing(c);
                    setFormOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" /> Düzenle
                </DropdownMenuItem>
                {!c.authable && (
                  <DropdownMenuItem onSelect={() => reissueMutation.mutate(c.id)}>
                    <Mail className="h-4 w-4" /> Yeni Davet Linki
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                  onSelect={() => setPendingDelete(c)}
                >
                  <Trash2 className="h-4 w-4" /> Sil
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [reissueMutation],
  );

  return (
    <>
      <PageHeader
        title="Müşteriler"
        description="Müşteri kayıtları ve aktivasyon durumları"
        actions={
          <Button
            onClick={() => {
              setEditing(undefined);
              setFormOpen(true);
            }}
          >
            <Plus className="h-4 w-4" /> Yeni Müşteri
          </Button>
        }
      />
      <div className="p-6 space-y-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Şirket adı veya e-posta ara…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>
        <DataTable
          columns={columns}
          data={customers.data?.data}
          loading={customers.isLoading}
          getRowId={(row) => row.id}
          empty={
            <EmptyState
              icon={UsersIcon}
              title="Henüz müşteri yok"
              description="İlk müşteriyi ekleyerek başlayın."
              action={
                <Button
                  onClick={() => {
                    setEditing(undefined);
                    setFormOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" /> Müşteri Ekle
                </Button>
              }
            />
          }
          pagination={
            customers.data
              ? {
                  page: customers.data.page,
                  limit: customers.data.limit,
                  total: customers.data.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>

      <CustomerFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        customer={editing}
        onCreated={(resp) => setInvitation(resp)}
      />

      {invitation && (
        <InvitationLinkDialog
          open
          onOpenChange={() => setInvitation(null)}
          activationUrl={invitation.activationUrl}
          expiresAt={invitation.expiresAt}
        />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Müşteri silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{pendingDelete?.companyName}</strong> arşivlenecek (yumuşak silme). Mevcut
              ödeme ve kimlik bilgisi kayıtları korunur ancak listelerden gizlenir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) removeMutation.mutate(pendingDelete.id);
                setPendingDelete(null);
              }}
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const Route = createFileRoute('/_app/admin/customers/')({ component: CustomersPage });
